import AudioManager from './AudioManager.js';
import Debug from '../utils/Debug.js';

/**
 * AudioSystemIntegrator - Bridges the SS2 AudioManager with the Trixel game loop.
 *
 * Responsibilities:
 *   - Initialize AudioManager on first user gesture
 *   - Update listener position each frame (derived from player + camera angle)
 *   - Schedule footsteps synced to animation frames (foot-strike frames)
 *   - Provide API hooks for combat sounds, ambient, etc.
 */
export default class AudioSystemIntegrator {
    constructor() {
        this.audio = new AudioManager();
        this._initialized = false;
        this._gestureHooked = false;
        this._musicStarted = false;

        // Per-character footstep tracking: Map<characterId, { lastFrame, footIndex }>
        this._footstepState = new Map();

        // Adaptive music state
        this._playerIdleTime = 0;
        this._combatCooldown = 0;
        this._currentMusicMood = 'explore';
        this._pendingMusicMood = 'explore';
        this._moodHoldTime = 0; // how long the derived mood has been stable

        // Foot-strike frames for each animation type.
        // One full loop = 2 footsteps (left foot + right foot).
        // These are the frames where a foot contacts the ground.
        this._footStrikeFrames = {
            8: [1, 5],  // 8-frame anims: strike at frames 1 and 5
            6: [1, 4],  // 6-frame anims: strike at frames 1 and 4
        };

        // Hook first user gesture to initialize audio
        this._hookUserGesture();
    }

    _hookUserGesture() {
        if (this._gestureHooked) return;
        this._gestureHooked = true;

        const handler = async () => {
            if (this._initialized) return;
            try {
                const seed = (Date.now() & 0xffff);
                await this.audio.initialize(seed);
                this._initialized = true;
                await this.audio.startMusic();
                this._musicStarted = true;
            } catch (e) {
                Debug.warn('Audio', 'Initialization failed:', e);
            }
        };

        const events = ['pointerdown', 'touchstart', 'mousedown', 'keydown'];
        const onGesture = () => {
            handler();
        };
        events.forEach(ev => window.addEventListener(ev, onGesture, { passive: true }));
    }

    /**
     * Called every frame from the game loop.
     * @param {number} deltaTime - Seconds since last frame
     * @param {Object} game - The Game instance (has player, camera, world, etc.)
     */
    update(deltaTime, game) {
        if (!this._initialized || !this.audio.isInitialized) return;
        if (!game) return;

        const player = game.player;
        const camera = game.camera;

        // Update listener position at player location, with camera-derived forward
        if (player && camera) {
            const angleRad = (camera.angle || 0) * Math.PI / 180;
            const forward = [Math.sin(angleRad), 0, Math.cos(angleRad)];
            this.audio.updateListener(
                [player.x, player.y + 1, player.z],
                forward
            );
        }

        // Adaptive music mood based on gameplay state
        if (player && this.audio.musicGenerator) {
            this._updateMusicMood(deltaTime, player);
        }

        // Schedule footsteps for player
        if (player) {
            this._updateCharacterFootsteps(player, 'player');
        }

        // Schedule footsteps for all NPCs
        if (game.world && game.world.characters) {
            for (const character of game.world.characters.values()) {
                if (character === player) continue;
                this._updateCharacterFootsteps(character, 'npc');
            }
        }
    }

    /**
     * Determine if the current animation is a walk or run cycle.
     * Returns { isLocomotion, frameCount } or null.
     */
    _getLocomotionInfo(character) {
        const animator = character.animator;
        if (!animator) return null;

        const anim = animator.currentState;
        if (!anim) return null;

        // Check if this is a walk/run animation
        const isWalk = anim.includes('walk') || anim.includes('walking');
        const isRun = anim.includes('run') || anim.includes('running');
        if (!isWalk && !isRun) return null;

        // Get frame count from atlas or metadata
        let frameCount = 0;
        if (animator.atlas) {
            frameCount = animator.atlas.getFrameCount(anim, animator.currentDirection);
        } else if (animator.metadata) {
            frameCount = animator.metadata.getFrameCount(anim, animator.currentDirection);
        }

        if (frameCount <= 0) return null;

        return { isWalk, isRun, frameCount };
    }

    /**
     * Animation-driven footstep scheduling.
     * Triggers footstep sounds at specific "foot strike" frames in the walk/run cycle.
     */
    _updateCharacterFootsteps(character, source) {
        const id = character.id || 'unknown';
        const animator = character.animator;

        // Must be grounded and have an animator
        const grounded = character.isGrounded !== false;
        if (!grounded || !animator) return;

        // Must be in a locomotion animation
        const loco = this._getLocomotionInfo(character);
        if (!loco) return;

        const currentFrame = animator.currentFrame;
        const now = performance.now();

        // Get or create per-character state (never deleted — prevents
        // rapid re-triggers when joystick jitter causes walk/idle toggling)
        let state = this._footstepState.get(id);
        if (!state) {
            state = { lastFrame: -1, footIndex: 0, lastTriggerTime: 0 };
            this._footstepState.set(id, state);
        }

        // Only trigger on frame change
        if (currentFrame === state.lastFrame) return;
        const prevFrame = state.lastFrame;
        state.lastFrame = currentFrame;

        // Check if any foot-strike frame was crossed between prevFrame and currentFrame.
        // At low FPS the animator can skip multiple frames per update, so we must
        // check the entire range, not just the landing frame.
        const strikes = this._footStrikeFrames[loco.frameCount] || this._footStrikeFrames[8];
        let hitStrike = false;
        if (prevFrame < 0) {
            // First frame — just check currentFrame
            hitStrike = strikes.includes(currentFrame);
        } else if (currentFrame > prevFrame) {
            // Normal forward advance (no loop wrap)
            for (let f = prevFrame + 1; f <= currentFrame; f++) {
                if (strikes.includes(f)) { hitStrike = true; break; }
            }
        } else {
            // Animation looped: prevFrame → end, then 0 → currentFrame
            const frameCount = loco.frameCount;
            for (let f = prevFrame + 1; f < frameCount; f++) {
                if (strikes.includes(f)) { hitStrike = true; break; }
            }
            if (!hitStrike) {
                for (let f = 0; f <= currentFrame; f++) {
                    if (strikes.includes(f)) { hitStrike = true; break; }
                }
            }
        }
        if (!hitStrike) return;

        // Minimum cooldown between footsteps (prevents double-triggers from
        // animation state resets caused by mobile joystick input jitter)
        const minInterval = loco.isRun ? 120 : 180; // ms
        if (now - state.lastTriggerTime < minInterval) return;
        state.lastTriggerTime = now;

        // Alternate foot index for L/R variation
        state.footIndex = (state.footIndex + 1) % 2;

        // Play footstep with variation
        this.audio.playWorldFootstep({
            worldPos: { x: character.x, y: character.y, z: character.z },
            source: source,
            characterId: id,
            isHeavy: loco.isRun,
            sideSign: state.footIndex === 0 ? -1 : 1,
            npcType: character.characterName || ''
        });
    }

    /**
     * Derive the target music mood from player state and update the generator.
     */
    _updateMusicMood(deltaTime, player) {
        // Track idle time
        if (player.isMoving) {
            this._playerIdleTime = 0;
        } else {
            this._playerIdleTime += deltaTime;
        }

        // Tick combat cooldown
        if (this._combatCooldown > 0) {
            this._combatCooldown -= deltaTime;
        }

        // Derive target mood from game state
        let targetMood = 'explore';
        if (this._combatCooldown > 0) {
            targetMood = 'combat';
        } else if (player.isRunning) {
            targetMood = 'adventure';
        } else if (!player.isMoving && this._playerIdleTime > 5) {
            targetMood = 'chill';
        }

        // Hysteresis: mood must be sustained briefly before sending to music generator
        // This prevents rapid walk/run toggling from spamming mood changes
        if (targetMood !== this._pendingMusicMood) {
            this._pendingMusicMood = targetMood;
            this._moodHoldTime = 0;
        } else {
            this._moodHoldTime += deltaTime;
        }

        // Combat is immediate (always important), others need ~0.5s hold
        const holdThreshold = targetMood === 'combat' ? 0 : 0.5;

        if (this._moodHoldTime >= holdThreshold && targetMood !== this._currentMusicMood) {
            this._currentMusicMood = targetMood;
            this.audio.musicGenerator.setAutoMoodMode(false);
            this.audio.musicGenerator.setMood(targetMood);
        }
    }

    /**
     * Play a combat swing sound at the character's position.
     * @param {Object} character - The attacking character
     * @param {string} weaponType - 'punch'|'kick'|'sword'|'blunt' etc.
     * @param {boolean} isPlayer - Whether this is the player (non-spatial) or NPC (spatial)
     */
    playAttackSound(character, weaponType = 'blunt', isPlayer = false) {
        if (!this._initialized || !character) return;

        // Trigger combat music mood
        this._combatCooldown = 8;

        this.audio.playWorldWeaponSwing({
            worldPos: { x: character.x, y: character.y + 1, z: character.z },
            weaponType: weaponType,
            source: isPlayer ? 'player' : 'npc'
        });
    }

    /**
     * Play a jump sound for a character.
     */
    playJumpSound(character, isPlayer = false) {
        if (!this._initialized || !character) return;
        this.audio.playJump({
            worldPos: { x: character.x, y: character.y, z: character.z },
            source: isPlayer ? 'player' : 'npc'
        });
    }

    /**
     * Play a landing sound for a character, scaled by drop height.
     * @param {number} dropHeight - How far the character fell (in world units)
     */
    playLandingSound(character, isPlayer = false, dropHeight = 0) {
        if (!this._initialized || !character) return;
        this.audio.playLanding({
            worldPos: { x: character.x, y: character.y, z: character.z },
            source: isPlayer ? 'player' : 'npc',
            dropHeight: dropHeight
        });
    }

    /**
     * Play a melee impact (hit) sound at the target's position.
     */
    playHitSound(target, attacker, damage, isPlayerTarget) {
        if (!this._initialized) return;
        const pos = { x: target.x, y: target.y + 1, z: target.z };

        this.audio.playWorldMeleeImpact({
            worldPos: pos,
            targetType: target.characterName || '',
            source: isPlayerTarget ? 'player' : 'npc',
            damage: damage,
            maxHealth: target.combatStats?.maxHealth || 100,
            isLethal: target.combatStats?.health <= 0
        });

        // Pain vocal for non-lethal hits on NPCs
        if (target.combatStats?.health > 0 && target.isNPC) {
            this.audio.playWorldNpcPain({
                worldPos: pos,
                npcType: target.characterName || '',
                damage: damage,
                maxHealth: target.combatStats?.maxHealth || 100
            });
        }

        this._combatCooldown = 8;
    }

    /**
     * Play a death sound for a character.
     */
    playDeathSound(target, isPlayer) {
        if (!this._initialized) return;
        const pos = { x: target.x, y: target.y + 1, z: target.z };
        if (isPlayer) {
            this.audio.playWorldPlayerDeath({ worldPos: pos });
        } else {
            this.audio.playWorldNpcDeath({
                worldPos: pos,
                npcType: target.characterName || ''
            });
        }
    }

    /**
     * Play an enemy attack vocal sound.
     */
    playEnemyAttackSound(character) {
        if (!this._initialized) return;
        this.audio.playWorldNpcAttackVocal({
            worldPos: { x: character.x, y: character.y + 1, z: character.z },
            npcType: character.characterName || ''
        });
        this._combatCooldown = 8;
    }

    /**
     * Play the fireball in-flight whoosh. Returns a handle with setGain(0-1) and stop().
     */
    playFireballFly(x, y, z) {
        if (!this._initialized) return null;
        return this.audio.playFireballFly({ worldPos: { x, y, z } });
    }

    /**
     * Play the fireball explosion sound at impact position.
     */
    playFireballExplosion(x, y, z) {
        if (!this._initialized) return;
        this.audio.playFireballExplosion({ worldPos: { x, y, z } });
        this._combatCooldown = 8;
    }

    /**
     * Play an item pickup sound based on item type.
     * @param {string} itemType - 'consumable'|'weapon'|'armor'|'key'|'quest'
     */
    playPickupSound(itemType) {
        if (!this._initialized) return;
        this.audio.playPickupSound(itemType);
    }

    /**
     * Get the underlying AudioManager for direct access.
     */
    getAudioManager() {
        return this.audio;
    }

    /**
     * Clean up all audio resources.
     */
    cleanup() {
        if (this.audio) {
            this.audio.cleanup();
        }
        this._footstepState.clear();
    }
}
